import { Box } from '@mui/material';
import { companyDailyNavSettingsUrl, companySummaryUrl } from 'common/config/urls';
import { Rows } from 'common/types/scalarSpreadsheet';
import { ValueWithLinkViewer } from 'components';
import CompanyRulesIndicator from 'pages/Intelligence/DailyNav/components/CompanyRulesIndicator';
import {
  DAILY_NAV_SPREADSHEET_INDENTED_CLASSNAME,
  DAILY_NAV_SPREADSHEET_NAME,
  DAILY_NAV_SPREADSHEET_SECTIONS_ALIAS,
  DAILY_NAV_SPREADSHEET_TITLE,
  DAILY_NAV_SPREADSHEET_TITLE_ALIAS,
} from 'pages/Intelligence/DailyNav/daily-nav.constants';
import { DailyNavTableRow } from '../../useDailyNavSheet.types';

interface CreateRowsParams {
  firmSlug?: string;
  rows: DailyNavTableRow[];
}

const createRows = ({ firmSlug, rows }: CreateRowsParams): Rows => [
  {
    alias: DAILY_NAV_SPREADSHEET_SECTIONS_ALIAS,
    // table-header keeps the band row's background in sync with the column-label row;
    // the shell only applies it automatically at colTitleRow.
    className: `${DAILY_NAV_SPREADSHEET_NAME}__sections table-header`,
    gridType: 'string',
    readOnly: true,
    value: '',
  } as Rows[number],
  {
    alias: DAILY_NAV_SPREADSHEET_TITLE_ALIAS,
    // table-header here paints the title-column "Company" cell like its row's data cells,
    // which receive the class from colTitleRow.
    className: `${DAILY_NAV_SPREADSHEET_NAME}__title division-bottom-only table-header`,
    gridType: 'string',
    largeHeader: true,
    readOnly: true,
    value: DAILY_NAV_SPREADSHEET_TITLE,
  } as Rows[number],
  ...rows.map(row => {
    if (row.kind === 'fund_security') {
      return {
        alias: row.alias,
        className: DAILY_NAV_SPREADSHEET_INDENTED_CLASSNAME,
        gridType: 'string',
        parent: String(row.companyId),
        readOnly: true,
        value: (
          <Box data-testid={`daily-nav-fund-security-row-${row.alias}`}>
            {[row.fundName, row.securityName].filter(Boolean).join(' — ')}
          </Box>
        ),
      } as Rows[number];
    }

    const companyUrl = firmSlug && row.companySlug ? companySummaryUrl(firmSlug, row.companySlug) : '';
    // Undefined, not '': the indicator uses presence to decide whether it links.
    const rulesUrl =
      firmSlug && row.companySlug ? companyDailyNavSettingsUrl(firmSlug, row.companySlug) : undefined;
    const hasInvestments = Boolean(row.investments?.length);

    return {
      alias: row.alias,
      gridType: 'string',
      hasSubRows: hasInvestments,
      readOnly: true,
      showSubRows: hasInvestments,
      // Icon first: fixed width, so the names stay left-aligned with each other.
      value: (
        <Box display="flex" alignItems="center" gap={0.5} data-testid={`daily-nav-company-row-${row.companyId}`}>
          <CompanyRulesIndicator config={row.record?.config} settingsUrl={rulesUrl} />
          <ValueWithLinkViewer name={row.companyName} url={companyUrl} />
        </Box>
      ),
    } as Rows[number];
  }),
];

export default createRows;
